import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useSocialRegister } from "~/api/authentication/authentication";
import { SocialRegister200Status } from "~/api/model/socialRegister200Status";
import { SocialRegister201Status } from "~/api/model/socialRegister201Status";
import { SocialRegister403 } from "~/api/model/socialRegister403";
import {
  removeAllTokens,
  ROUTES,
  storeAccessToken,
  storeRefreshToken,
  storeUsername,
} from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import {
  clearSocialAuthFlags,
  exchangeCodeForTokens,
  LINK_SUCCESS_MESSAGE,
  parseOAuthCallback,
  retryOAuthAfterLinking,
} from "~/utils/auth/socialAuth";
import logger from "~/utils/logger";

type CallbackState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "link_success" }
  | { status: "registration_incomplete" };

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const { mutate: socialRegister } =
    useSocialRegister<AxiosError<SocialRegister403>>();
  const [state, setState] = useState<CallbackState>({ status: "loading" });

  useEffect(() => {
    const handleCallback = async () => {
      const { code, error, errorDescription, skipDemo } = parseOAuthCallback();

      if (error) {
        if (errorDescription?.includes(LINK_SUCCESS_MESSAGE)) {
          trackEvent({ eventName: "app/auth/social_login.account_linked" });
          removeAllTokens();

          if (retryOAuthAfterLinking()) {
            return;
          }

          setState({ status: "link_success" });
          return;
        }

        logger.error("OAuth callback error:", { error, errorDescription });
        trackEvent({ eventName: "app/auth/social_login.error" });
        setState({
          status: "error",
          message:
            errorDescription || "Authentication failed. Please try again.",
        });
        return;
      }

      if (!code) {
        setState({
          status: "error",
          message: "No authorization code received. Please try again.",
        });
        return;
      }

      try {
        const tokens = await exchangeCodeForTokens(code);

        // Extract username from idToken for token refresh functionality
        // Use proper Base64URL to Base64 conversion for JWT decoding
        const idTokenParts = tokens.idToken.split(".");
        let base64 = idTokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (base64.length % 4 !== 0) base64 += "=";
        const idTokenPayload = JSON.parse(atob(base64)) as Record<
          string,
          unknown
        >;
        const username =
          (idTokenPayload.email as string) ||
          (idTokenPayload["cognito:username"] as string);

        storeAccessToken(tokens.accessToken);
        storeRefreshToken(tokens.refreshToken);
        if (username) {
          storeUsername(username);
        }
        socialRegister(
          {
            data: {},
          },
          {
            onSuccess: (data) => {
              if (
                data.status === SocialRegister200Status.REGISTRATION_INCOMPLETE
              ) {
                trackEvent({
                  eventName: "app/auth/social_login.registration_incomplete",
                });
                setState({ status: "registration_incomplete" });
                navigate({
                  pathname: "/register/finish",
                  search: skipDemo
                    ? createSearchParams({ "skip-demo": "1" }).toString()
                    : undefined,
                });
                return;
              }
              if (data.status === SocialRegister200Status.ALREADY_REGISTERED) {
                clearSocialAuthFlags();
                trackEvent({ eventName: "app/auth/social_login.success" });
                navigate("/");
                return;
              }
              if (
                data.status === SocialRegister201Status.REGISTRATION_COMPLETE
              ) {
                clearSocialAuthFlags();
                trackEvent({
                  eventName: "app/auth/social_login.registration_complete",
                });
                navigate("/");
              }
            },
            onError: (error) => {
              const { status, data } = error.response ?? {};
              logger.error("Social registration failed", {
                status,
                code: data?.code,
              });
              trackEvent({ eventName: "app/auth/social_login.error" });
              setState({
                status: "error",
                message: data?.message || "Registration failed",
              });
            },
          }
        );
      } catch (err) {
        logger.error("Social auth error:", err);
        trackEvent({ eventName: "app/auth/social_login.error" });
        setState({
          status: "error",
          message:
            err instanceof Error
              ? err.message
              : "Authentication failed. Please try again.",
        });
      }
    };

    handleCallback();
  }, [navigate, socialRegister]);

  if (state.status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="auth.callback.loading"
            defaultMessage="Completing sign in..."
          />
        </p>
      </div>
    );
  }

  if (state.status === "link_success") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 max-w-md mx-auto text-center">
        <div className="rounded-full bg-green-100 p-3">
          <svg
            className="h-6 w-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold">
          <FormattedMessage
            id="auth.callback.linkSuccess.title"
            defaultMessage="Account Linked"
          />
        </h2>
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="auth.callback.linkSuccess.message"
            defaultMessage='Your account has been linked. Please click "Sign in" one last time to access your account.'
          />
        </p>
        <button
          onClick={() => navigate(ROUTES.LOGIN)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <FormattedMessage
            id="auth.callback.linkSuccess.button"
            defaultMessage="Go to Sign In"
          />
        </button>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 max-w-md mx-auto text-center">
        <div className="rounded-full bg-red-100 p-3">
          <svg
            className="h-6 w-6 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold">
          <FormattedMessage
            id="auth.callback.error.title"
            defaultMessage="Authentication Failed"
          />
        </h2>
        <p className="text-sm text-muted-foreground">{state.message}</p>
        <button
          onClick={() => navigate(ROUTES.LOGIN)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <FormattedMessage
            id="auth.callback.error.button"
            defaultMessage="Back to Sign In"
          />
        </button>
      </div>
    );
  }

  return null;
};
